/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { ComponentPropagateFormulaRequestMapper } from '../house-plans/mappers/hpe-details.mapper';
import { ComponentPropagateFormulaRequestDto } from '../house-plans/models/hpe-details.dto';
import {
  BulkUpdateComponentAddDto,
  BulkUpdateComponentDeleteDto,
  BulkUpdateComponentReplaceDto,
  BulkUpdateHousePlanAddDto,
  BulkUpdateHousePlanDeleteDto,
  BulkUpdateHousePlanReplaceDto,
  BulkUpdateItemComponentListItemDto,
  BulkUpdateReplaceInitDto,
  ButlListItemDto,
  ButlMessageDto,
} from './bulk-update.dto';
import {
  BulkUpdateComponentAddMapper,
  BulkUpdateComponentDeleteMapper,
  BulkUpdateComponentReplaceMapper,
  BulkUpdateHousePlanAddMapper,
  BulkUpdateHousePlanDeleteMapper,
  BulkUpdateHousePlanReplaceMapper,
  BulkUpdateItemComponentListItemMapper,
  BulkUpdateReplaceInitMapper,
  ButlListItemMapper,
} from './bulk-update.mapper';

@Injectable()
export class BulkUpdateRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  loadItemsComponentsByHp(id: ID): Observable<BulkUpdateItemComponentListItemDto[]> {
    return this.httpGet(
      `${this.bp}/bulk-update/item-and-component/list/${id}`,
      arrayOf(BulkUpdateItemComponentListItemMapper));
  }

  addHpSave(addHp: BulkUpdateHousePlanAddDto): Observable<any> {
    return this.httpPut(`${this.bp}/bulk-update/process/hp-add`, addHp, BulkUpdateHousePlanAddMapper);
  }

  addComponentSave(addComponent: BulkUpdateComponentAddDto): Observable<any> {
    return this.httpPut(`${this.bp}/bulk-update/process/component-add`, addComponent, BulkUpdateComponentAddMapper);
  }

  replaceHpSave(replaceHp: BulkUpdateHousePlanReplaceDto): Observable<any> {
    return this.httpPut(`${this.bp}/bulk-update/process/hp-replace`, replaceHp, BulkUpdateHousePlanReplaceMapper);
  }

  replaceComponentSave(replaceComponent: BulkUpdateComponentReplaceDto): Observable<any> {
    return this.httpPut(`${this.bp}/bulk-update/process/component-replace`, replaceComponent, BulkUpdateComponentReplaceMapper);
  }

  deleteHpSave(deleteHp: BulkUpdateHousePlanDeleteDto): Observable<any> {
    return this.httpPut(`${this.bp}/bulk-update/process/hp-delete`, deleteHp, BulkUpdateHousePlanDeleteMapper);
  }

  deleteComponentSave(deleteComponent: BulkUpdateComponentDeleteDto): Observable<any> {
    return this.httpPut(`${this.bp}/bulk-update/process/component-delete`, deleteComponent, BulkUpdateComponentDeleteMapper);
  }

  getReplaceInitData(): Observable<BulkUpdateReplaceInitDto> {
    return this.httpGet(`${this.bp}/bulk-update/replace-init`, BulkUpdateReplaceInitMapper);
  }

  propagateFormula(request: ComponentPropagateFormulaRequestDto): Observable<any> {
    return this.httpPut(
      `${this.bp}/bulk-update/propagate-formula`,
      request,
      ComponentPropagateFormulaRequestMapper);
  }

  getDetailedMessage(id: ID): Observable<ButlMessageDto> {
    return this.httpGet(`${this.bp}/bulk-update/${id}`, identity());
  }

  findAllLogsAsDataSource(filter: any): SlicedDataSource<ButlListItemDto> {
    const req = {
      searchString: filter.searchString,
      startDate: filter.startDate,
      endDate: filter.endDate,
    };

    return this.createSlicedDataSourceGet(`${this.bp}/bulk-update`, req, undefined, arrayOf(ButlListItemMapper));
  }
}
